import CreateButton from "../components/CreateButton"

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { ButtonStatus } from '../common/constants';
import { Each } from '../common/Each';
import StatusBadge from '../components/StatusBadge';
import EmptyContent from '../components/EmptyContent';
import Button from '../components/Button';
import Card from "../components/cards/Card";
import Dialog from '../components/dialogs/Dialog';
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import MultipleSelect from '../components/MultipleSelect';
import Select from '../components/Select';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import TextInput from '../components/TextInput';
import Toggle from '../components/Toggle';
import { formatDateV2, formatPrice, formatTimeV2, validateEmail } from '../utils';
import styles from './Coupons.module.css';
import { Helmet, HelmetProvider } from 'react-helmet-async';

const Coupons = () => {

    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [coupons, setCoupons] = useState({ data: [] });
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [deactivation, setDeactivation] = useState(null);
    const [deactivating, setDeactivating] = useState(false);
    const [deactivationError, setDeactivationError] = useState('');
    const deactivationPending = useRef(false);
    const [cursors, setCursors] = useState([null]);
    const [page, setPage] = useState(0);
    const [refresh, setRefresh] = useState(0);
    const listRequest = useRef(0);
    const detailRequest = useRef(0);
    const cursor = cursors[page];
    const [couponDialog, setCouponDialog] = useState({
        open: false,
        coupon: null
    })

    const [promoCodeDialog, setPromoCodeDialog] = useState({
        open: false,
        promoCode: null
    })

    const [couponName, setCouponName] = useState('');
    const [couponType, setCouponType] = useState('percent');
    const [couponValue, setCouponValue] = useState('');
    const [couponProducts, setCouponProducts] = useState([]);

    const [promoCodeName, setPromoCodeName] = useState('');
    const [promoCodeExpires, setPromoCodeExpires] = useState(false);
    const [promoCodeExpiresAt, setPromoCodeExpiresAt] = useState('');
    const [promoCodeCustomer, setPromoCodeCustomer] = useState(false);
    const [promoCodeCustomerEmail, setPromoCodeCustomerEmail] = useState('');
    const [promoCodeEmailError, setPromoCodeEmailError] = useState(null);

    const [couponLoading, setCouponLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [formError, setFormError] = useState('');
    const [query, setQuery] = useState('');
    const [couponButtonStatus, setCouponButtonStatus] = useState(null);
    const [promoCodeButtonStatus, setPromoCodeButtonStatus] = useState(null);

    const couponTypes = [
        { id: 0, name: 'Percentuale', value: 'percent' },
        { id: 1, name: 'Fisso', value: 'fixed' }
    ];

    const getCoupons = useCallback(async () => {
        const request = ++listRequest.current;
        setCouponLoading(true);
        setLoadError('');
        try {
            const result = await api.get('/admin/coupons', { params: { limit: 20, starting_after: cursor || undefined, include_promo_codes: false } });
            if (request === listRequest.current) setCoupons(result);
        } catch (error) {
            if (request === listRequest.current) {
                setCoupons({ data: [], has_more: false });
                setLoadError('Impossibile caricare gli sconti. Riprova tra qualche istante.');
            }
        } finally {
            if (request === listRequest.current) setCouponLoading(false);
        }
    }, [cursor]);

    useEffect(() => {
        getCoupons();
        return () => { listRequest.current += 1; };
    }, [getCoupons, refresh]);

    const openDetails = async (coupon) => {
        const request = ++detailRequest.current;
        setSelectedCoupon(coupon);
        setDetailLoading(true);
        setDetailError('');
        try {
            const detail = await api.get(`/admin/coupons/${encodeURIComponent(coupon.id)}`);
            if (request === detailRequest.current) setSelectedCoupon(detail);
        } catch (error) {
            if (request === detailRequest.current) setDetailError('Impossibile caricare i dettagli dello sconto.');
        } finally {
            if (request === detailRequest.current) setDetailLoading(false);
        }
    };

    useEffect(() => {

        const getCoursesProducts = async () => {
            try {
                let courses = await api.get('/admin/courses?complete=false');
                setProducts(courses.filter(c => c.product?.stripe_id).map(c => {
                    return {
                        id: c.product?.stripe_id ?? '',
                        name: c.name,
                    };
                }).sort((a, b) => a.name.localeCompare(b.name)));
            }
            catch (e) {
                console.error(e);
            }
        }

        getCoursesProducts();
    }, []);

    const createCoupon = async () => {
        if (!couponValid || couponButtonStatus === ButtonStatus.Loading) return;
        setFormError('');
        setCouponButtonStatus(ButtonStatus.Loading)
        let data = {
            name: couponName.trim(),
            duration: 'forever'
        }

        if (couponType === 'percent') {
            data.percent_off = parseFloat(couponValue);
        }
        else {
            data.amount_off = Math.round(Number(couponValue) * 100); // Convert to cents
        }

        if (couponProducts.length > 0) {
            data.applies_to = {
                products: couponProducts
            };
        }

        try {
            let coupon = await api.post('/admin/coupons', data);
            coupon.promo_codes = [];
            setCouponDialog({ open: false, coupon: null });
            coupon.products = products.filter(product => couponProducts.includes(product.id));
            setPage(0); setCursors([null]); setQuery(''); setRefresh(value => value + 1);
            setCouponButtonStatus(ButtonStatus.Success);
        }
        catch (e) {
            console.error(e);
            setCouponButtonStatus(ButtonStatus.Error);
            setFormError(typeof e.detail === 'string' ? e.detail : 'Impossibile creare lo sconto. Riprova.');
        }
    }

    const createPromoCode = async () => {
        if (!promoValid || promoCodeButtonStatus === ButtonStatus.Loading) return;
        setFormError('');
        setPromoCodeButtonStatus(ButtonStatus.Loading)
        try {
            let promoCodeData = {
                code: promoCodeName.trim(),
                coupon: promoCodeDialog.promoCode.coupon,
            };

            if (promoCodeExpires) {
                promoCodeData.expires_at = promoCodeExpiresAt;
            }

            if (promoCodeCustomer) {
                promoCodeData.customer_email = promoCodeCustomerEmail;
            }

            let promoCode = await api.post('/admin/promo_codes', promoCodeData);
            setPromoCodeDialog({ open: false, promoCode: null });
            setSelectedCoupon(current => current?.id === promoCodeData.coupon
                ? { ...current, promo_codes: [promoCode, ...(current.promo_codes || [])] } : current);
            setPromoCodeName('');
            setPromoCodeExpires(false);
            setPromoCodeExpiresAt('');
            setPromoCodeCustomer(false);
            setPromoCodeCustomerEmail('');
            setPromoCodeButtonStatus(ButtonStatus.Success);
        }
        catch (e) {
            console.error(e);
            setPromoCodeButtonStatus(ButtonStatus.Error);
            setFormError(typeof e.detail === 'string' ? e.detail : 'Impossibile creare il codice promozionale. Riprova.');
        }
    }

    const requestDeactivation = (kind, target) => {
        setDeactivationError('');
        setDeactivation({ kind, id: target.id, name: target.code || target.name || target.id });
    };

    const confirmDeactivation = async () => {
        if (!deactivation || deactivationPending.current) return;
        deactivationPending.current = true;
        setDeactivating(true);
        setDeactivationError('');
        try {
            if (deactivation.kind === 'coupon') {
                await api.delete(`/admin/coupons/${encodeURIComponent(deactivation.id)}`);
                detailRequest.current += 1;
                setSelectedCoupon(null);
                // Deleted coupons may have been pagination cursors: restart from page one.
                setCoupons(current => ({ ...current, data: current.data.filter(coupon => coupon.id !== deactivation.id) }));
                setCursors([null]); setPage(0); setQuery(''); setRefresh(value => value + 1);
            } else {
                const updated = await api.post(`/admin/promo_codes/${encodeURIComponent(deactivation.id)}/deactivate`);
                setSelectedCoupon(current => current ? {
                    ...current,
                    promo_codes: (current.promo_codes || []).map(promo => promo.id === deactivation.id
                        ? { ...promo, active: updated.active } : promo),
                } : current);
            }
            setDeactivation(null);
        } catch (error) {
            setDeactivationError(typeof error.detail === 'string' ? error.detail : 'Disattivazione non riuscita. Riprova.');
        } finally {
            deactivationPending.current = false;
            setDeactivating(false);
        }
    };

    const handlePromoCodeCustomerChange = (value) => {
        let isValid = validateEmail(value);
        if (value && value.length > 0 && !isValid) {
            setPromoCodeEmailError('Email non valida');
        }
        else {
            setPromoCodeEmailError(null);
        }
        setPromoCodeCustomerEmail(value);
        if (!value) {
            setPromoCodeCustomerEmail('');
        }
    }

    const couponValid = couponName.trim() && Number(couponValue) > 0 && Number.isFinite(Number(couponValue)) && (couponType !== 'percent' || Number(couponValue) <= 100);
    const promoValid = promoCodeName.trim() && (!promoCodeCustomer || validateEmail(promoCodeCustomerEmail)) && (!promoCodeExpires || (promoCodeExpiresAt && new Date(promoCodeExpiresAt + 'Z').getTime() > Date.now()));
    const visibleCoupons = (coupons.data || []).filter(coupon =>
        [coupon.name, coupon.id]
            .some(value => String(value || '').toLowerCase().includes(query.trim().toLowerCase())));
    const openPromoDialog = (couponId) => {
        setPromoCodeName('');
        setPromoCodeExpires(false);
        setPromoCodeExpiresAt('');
        setPromoCodeCustomer(false);
        setPromoCodeCustomerEmail('');
        setPromoCodeEmailError(null);
        setPromoCodeButtonStatus(null);
        setFormError('');
        setPromoCodeDialog({ open: true, promoCode: { coupon: couponId } });
    };

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Shop - Sconti</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <header className={styles.header}>
                            <div><p className={styles.eyebrow}>Shop</p><h1>Sconti</h1><p>Gestisci gli sconti e i codici promozionali del catalogo.</p></div>
                            <CreateButton onClick={() => {
                                setCouponName(''); setCouponValue(''); setCouponType('percent'); setCouponProducts([]);
                                setCouponButtonStatus(null); setFormError('');
                                setCouponDialog({ open: true, coupon: null });
                            }}>
                                Aggiungi sconto

                            </CreateButton>
                        </header>
                        <Card appearance="modern" integratedTable title="Elenco sconti" actions={<span className={styles.resultCount} role="status">{couponLoading ? 'Caricamento…' : `${visibleCoupons.length} sconti in questa pagina`}</span>}>
                            <div className={styles.toolbar}><TextInput appearance="modern" type="search" placeholder="Cerca negli sconti di questa pagina" value={query} onKeyUp={setQuery} /></div>
                            {loadError && <div className={styles.error} role="alert">{loadError} <Button appearance="subtle" onClick={getCoupons}>Riprova</Button></div>}
                            {!couponLoading && !loadError && visibleCoupons.length === 0 && <EmptyContent title="Nessuno sconto trovato" message="Aggiungi uno sconto o modifica la ricerca." />}
                            <Table appearance="minimal" loading={couponLoading} loadingN={8}
                                style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
                                defaultSort={{ key: 'created', direction: 'desc' }}
                                onRowClick={row => openDetails(row.coupon)}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'string' },
                                    { key: 'name', label: 'Nome', type: 'string', wrap: true },
                                    { key: 'discount', label: 'Sconto', type: 'string' },
                                    { key: 'productCount', label: 'Prodotti', type: 'string' },
                                    { key: 'date', label: 'Data di creazione', type: 'string' },
                                    { key: 'status', label: 'Stato', type: 'custom' },
                                ]}
                                elements={visibleCoupons.map(coupon => ({
                                    id: coupon.id, name: coupon.name, coupon, created: coupon.created,
                                    discount: coupon.percent_off != null ? `${coupon.percent_off}%` : `€ ${formatPrice(coupon.amount_off / 100)}`,
                                    productCount: coupon.product_count ? `${coupon.product_count} ${coupon.product_count === 1 ? 'prodotto' : 'prodotti'}` : 'Tutti i prodotti',
                                    date: formatDateV2(new Date(coupon.created * 1000).toISOString(), { day: 'numeric', month: 'numeric', year: 'numeric' }),
                                    status: <StatusBadge appearance="soft" label={coupon.valid ? 'Valido' : 'Non valido'} color={coupon.valid ? 'var(--sf-green)' : 'var(--sf-red)'} />,
                                }))}
                            />
                            <div className={styles.pagination}>
                                <Pagination page={page} per={20} itemCount={coupons.data.length}
                                    hasNextPage={!loadError && Boolean(coupons.has_more)} loading={couponLoading}
                                    onChange={nextPage => {
                                        if (nextPage > page) {
                                            setCursors(current => [...current.slice(0, page + 1), coupons.data[coupons.data.length - 1].id]);
                                        }
                                        setQuery(''); setPage(nextPage);
                                    }} />
                            </div>
                        </Card>
                    </div>
                </div>

                <Dialog appearance="modern" open={Boolean(selectedCoupon) && !promoCodeDialog.open && !deactivation}
                    title={selectedCoupon?.name || 'Dettagli sconto'}
                    onClose={() => { detailRequest.current += 1; setSelectedCoupon(null); }}
                    action={selectedCoupon && <div className={styles.detailActions}>
                        <Button requiresEdit appearance="subtle" additionalClass={styles.deactivateButton} disabled={detailLoading || Boolean(detailError)} onClick={() => requestDeactivation('coupon', selectedCoupon)}>Disattiva sconto</Button>
                        <Button additionalClass={styles.addCodeButton} accentColor="var(--primary)" disabled={detailLoading || Boolean(detailError) || !selectedCoupon.valid} onClick={() => openPromoDialog(selectedCoupon.id)}>Aggiungi codice</Button>
                    </div>}>
                    {selectedCoupon && <div className={styles.details}>
                        <div className={styles.detailSummary}>
                            <div><span>ID</span><strong>{selectedCoupon.id}</strong></div>
                            <div><span>Sconto</span><strong>{selectedCoupon.percent_off != null ? `${selectedCoupon.percent_off}%` : `€ ${formatPrice(selectedCoupon.amount_off / 100)}`}</strong></div>
                            <div><span>Creato il</span><strong>{formatDateV2(new Date(selectedCoupon.created * 1000).toISOString(), { day: 'numeric', month: 'numeric', year: 'numeric' })}</strong></div>
                            <StatusBadge appearance="soft" label={selectedCoupon.valid ? 'Valido' : 'Non valido'} color={selectedCoupon.valid ? 'var(--sf-green)' : 'var(--sf-red)'} />
                        </div>
                        {detailLoading && <p role="status">Caricamento dettagli…</p>}
                        {detailError && <div className={styles.error} role="alert">{detailError}<Button appearance="subtle" onClick={() => openDetails(selectedCoupon)}>Riprova</Button></div>}
                        {!detailLoading && !detailError && <>
                            <section className={styles.detailSection}><h3>Prodotti associati</h3>
                                {(selectedCoupon.applies_to?.products || []).length > 0 ? <ul className={styles.productList}>
                                    {selectedCoupon.applies_to.products.map(id => <li key={id}>{selectedCoupon.products?.find(product => product.stripe_id === id || product.id === id)?.name || products.find(product => product.id === id)?.name || id}</li>)}
                                </ul> : <p>Lo sconto è valido per tutti i prodotti.</p>}
                            </section>
                            <section className={styles.detailSection}><h3>Codici promozionali ({selectedCoupon.promo_codes?.length || 0})</h3>
                                {selectedCoupon.promo_codes?.length > 0 ? <div className={styles.promoList}>
                                    <Each of={selectedCoupon.promo_codes} render={promo => <article className={styles.promoCard} key={promo.id}>
                                        <div className={styles.promoHeading}><strong>{promo.code}</strong><StatusBadge appearance="soft" label={promo.active ? 'Attivo' : 'Inattivo'} color={promo.active ? 'var(--sf-green)' : 'var(--sf-red)'} /></div>
                                        <dl className={styles.promoFields}>
                                            <div><dt>Creato il</dt><dd>{formatDateV2(new Date(promo.created * 1000).toISOString(), { day: 'numeric', month: 'numeric', year: 'numeric' })}</dd></div>
                                            <div><dt>Scadenza</dt><dd>{promo.expires_at ? `${formatDateV2(new Date(promo.expires_at * 1000).toISOString(), { day: 'numeric', month: 'numeric', year: 'numeric' })} · ${formatTimeV2(new Date(promo.expires_at * 1000).toISOString())}` : 'Illimitata'}</dd></div>
                                            <div><dt>Associato a</dt><dd>{promo.user ? <button className={styles.userLink} onClick={() => navigate(`/users/students/${promo.user.id}`)}>{promo.user.name} {promo.user.surname} · {promo.user.email}</button> : promo.customer ? 'Cliente riservato' : 'Tutti gli utenti'}</dd></div>
                                            <div><dt>Riscatti</dt><dd>{promo.times_redeemed}</dd></div>
                                        </dl>
                                        {promo.active && <div className={styles.promoActions}>
                                            <Button requiresEdit appearance="subtle" additionalClass={styles.deactivateButton} onClick={() => requestDeactivation('promo', promo)}>Disattiva codice</Button>
                                        </div>}
                                    </article>} />
                                </div> : <p>Nessun codice promozionale. Crea il primo codice per questo sconto.</p>}
                            </section>
                        </>}
                    </div>}
                </Dialog>

                <Dialog appearance="modern" style={{ width: 'min(560px, 100%)' }} open={Boolean(deactivation)}
                    title={deactivation?.kind === 'coupon' ? 'Disattiva definitivamente lo sconto' : 'Disattiva codice promozionale'}
                    onClose={() => { if (!deactivationPending.current) setDeactivation(null); }}
                    action={<div className={styles.confirmActions}>
                        <Button appearance="subtle" disabled={deactivating} onClick={() => setDeactivation(null)}>Annulla</Button>
                        <Button requiresEdit additionalClass={styles.addCodeButton} accentColor="var(--sf-red)" disabled={deactivating}
                            status={deactivating ? ButtonStatus.Loading : null} onClick={confirmDeactivation}>
                            {deactivation?.kind === 'coupon' ? 'Disattiva definitivamente' : 'Conferma disattivazione'}
                        </Button>
                    </div>}>
                    <div className={styles.confirmContent}>
                        <strong>{deactivation?.name}</strong>
                        <p>{deactivation?.kind === 'coupon'
                            ? 'Stripe eliminerà definitivamente questo sconto. Non potrà essere riattivato e i codici promozionali associati non saranno più utilizzabili. Gli sconti già applicati agli acquisti resteranno invariati.'
                            : 'Il codice non potrà più essere utilizzato per nuovi acquisti. Gli utilizzi precedenti e gli altri codici dello sconto resteranno invariati.'}</p>
                        {deactivationError && <div className={styles.error} role="alert">{deactivationError}</div>}
                    </div>
                </Dialog>

                <Dialog
                    appearance="modern"
                    style={{ width: 'min(640px, 100%)' }}
                    open={couponDialog.open}
                    onClose={() => { if (couponButtonStatus !== ButtonStatus.Loading) setCouponDialog({ open: false, coupon: null }); }}
                    title={couponDialog.coupon ? 'Modifica Sconto' : 'Crea Sconto'}
                    action={
                        <Button onClick={createCoupon}
                            disabled={!couponValid || couponButtonStatus === ButtonStatus.Loading}
                            status={couponButtonStatus}>Crea sconto</Button>
                    }
                    >
                    <div className={styles.couponDialog}>
                        {formError && <div className={styles.error} role="alert">{formError}</div>}
                        <div className={styles.couponDialogRow}>
                            <TextInput appearance="modern" type="text" label="Nome" placeholder="Nome" value={couponName} onKeyUp={(v) => setCouponName(v)} />
                        </div>
                        <div className={styles.couponDialogRow}>
                            <TextInput appearance="modern" type="number" numberType='float' label={couponType === 'fixed' ? 'Importo (€)' : 'Percentuale (%)'} placeholder={couponType === 'fixed' ? "Sconto Fisso (€)" : "Sconto Percentuale (%)"} value={couponValue} onKeyUp={(v) => {
                                setCouponValue(v);
                            }} />
                            <div className={styles.formField}><span>Tipo di sconto</span><Select appearance="modern" portal selected={couponTypes.find(t => couponType === t.value)} options={couponTypes} onSelect={(item) => setCouponType(item.value)} placeholder="Tipo di sconto" /></div>
                        </div>
                        <div className={styles.couponDialogRow}>
                            <div className={styles.formField}><span>Prodotti a cui applicare lo sconto</span><MultipleSelect appearance="modern" portal placeholder={"Tutti i prodotti"} selected={products.filter(p => couponProducts.includes(p.id))} options={products} onSelect={(items) => {
                                if (items.length === products.length) {
                                    setCouponProducts([]);
                                }
                                else {
                                    setCouponProducts(items.map(i => i.id));
                                }
                            }} /></div>
                        </div>

                    </div>
                </Dialog>

                <Dialog
                    appearance="modern"
                    style={{ width: 'min(640px, 100%)' }}
                    open={promoCodeDialog.open}
                    onClose={() => { if (promoCodeButtonStatus !== ButtonStatus.Loading) setPromoCodeDialog({ open: false, promoCode: null }); }}
                    title={promoCodeDialog.promoCode?.id ? 'Modifica codice promozionale' : 'Crea codice promozionale'}
                    action={
                        <Button
                            disabled={!promoValid || promoCodeButtonStatus === ButtonStatus.Loading}
                            status={promoCodeButtonStatus}
                            onClick={createPromoCode}
                        >Crea codice promozionale</Button>
                    }
                    >
                    <div className={styles.couponDialog}>
                        {formError && <div className={styles.error} role="alert">{formError}</div>}
                        <div className={styles.couponDialogRow}>
                            <TextInput appearance="modern" type="text" label="Codice promozionale" placeholder="Es. BENVENUTO20" value={promoCodeName} onKeyUp={(v) => setPromoCodeName(v)} />
                        </div>
                        <div className={styles.couponDialogRow}>
                            <div className={styles.toggleWrapper}>
                                <span>Fai scadere il codice</span>

                                <Toggle value={promoCodeExpires} onChange={(v) => {
                                    setPromoCodeExpires(v);
                                    if (!v) {
                                        setPromoCodeExpiresAt('');
                                    }
                                }} />
                            </div>
                        </div>
                        <div className={styles.couponDialogRow}>
                            {promoCodeExpires &&
                                <TextInput appearance="modern" type="date" placeholder={"Scadenza"} value={promoCodeExpiresAt ? new Date(promoCodeExpiresAt + 'Z') : null} onKeyUp={(d) => {
                                    setPromoCodeExpiresAt(d && !Number.isNaN(d.getTime()) ? d.toISOString().split('.')[0] : '')
                                }} />
                            }
                        </div>
                        <div className={styles.couponDialogRow}>
                            <div className={styles.toggleWrapper}>
                                <span>Assegna a un cliente specifico</span>

                                <Toggle value={promoCodeCustomer} onChange={(v) => {
                                    setPromoCodeCustomer(v);
                                    if (!v) {
                                        setPromoCodeCustomerEmail('');
                                        setPromoCodeEmailError(null);
                                    }
                                }} />
                            </div>
                        </div>
                        {promoCodeCustomer &&
                            <>
                                <div className={styles.couponDialogRow}>
                                    <TextInput appearance="modern" type="text" label="Email cliente" placeholder="Email" error={!!promoCodeEmailError} value={promoCodeCustomerEmail} onKeyUp={(v) => handlePromoCodeCustomerChange(v)} />
                                </div>
                                {promoCodeEmailError &&
                                    <div className={styles.error} role="alert">
                                        {promoCodeEmailError}
                                    </div>
                                }
                            </>
                        }
                    </div>

                </Dialog>
            </div>
        </HeaderFooterLayout>
    )

}

export default Coupons;
